import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Slider from "react-slick";

interface SimilarProjectsSectionProps {
    similarProjects: any[];
    sliderRef: React.RefObject<any>;
    similarSliderSettings: any;
    getImageUrl: (url: string) => string;
    addToCompare: (p: any) => void;
    compareList: any[];
}

const NextArrow = (props: any) => {
    const { className, style, onClick } = props;
    return (
        <button
            className="absolute right-[5px] md:right-[10px] top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 bg-white rounded-full shadow-lg flex items-center justify-center z-10 hover:bg-accent text-primary-dark transition-all border border-gray-100"
            onClick={onClick}
        >
            <i className="fa-solid fa-chevron-right text-xs md:text-sm"></i>
        </button>
    );
}

const PrevArrow = (props: any) => {
    const { className, style, onClick } = props;
    return (
        <button
            className="absolute left-[-15px] md:left-[-30px] top-1/2 -translate-y-1/2 w-8 h-8 md:w-10 md:h-10 bg-white rounded-full shadow-lg flex items-center justify-center z-10 hover:bg-accent text-primary-dark transition-all border border-gray-100"
            onClick={onClick}
        >
            <i className="fa-solid fa-chevron-left text-xs md:text-sm"></i>
        </button>
    );
}

const SimilarProjectsSection: React.FC<SimilarProjectsSectionProps> = ({
    similarProjects,
    sliderRef,
    getImageUrl,
    addToCompare,
    compareList
}) => {

    if (similarProjects.length === 0) return null;

    const formatPrice = (price?: number | string) => {
        if (!price) return 'Price on Request';
        if (typeof price === 'string') return price;
        let formatted = '';
        if (price >= 10000000) {
            const cr = price / 10000000;
            formatted = `₹${cr.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Cr`;
        } else if (price >= 100000) {
            const lac = price / 100000;
            formatted = `₹${lac.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })} Lac`;
        } else {
            formatted = `₹${price.toLocaleString()}`;
        }
        return formatted;
    };

    const formatPriceRange = (item: any) => {
        if (item.overview?.priceRange) return item.overview.priceRange;
        if (item.minPrice && item.maxPrice) {
            return `${formatPrice(item.minPrice)} - ${formatPrice(item.maxPrice)}*`;
        }
        if (item.minPrice) return `${formatPrice(item.minPrice)}*`;
        return 'Price on Request';
    };

    const ProjectCard = ({ item }: { item: any }) => {
        const [isHovered, setIsHovered] = useState(false);
        const projectImage = item.featuredImage || item.coverImage || (item.images && item.images.length > 0 ? item.images[0] : '/assets/img/p-1.jpg');

        return (
            <div 
                className={`property-card-container group bg-white rounded-xl md:rounded-2xl overflow-hidden border border-gray-100 h-full flex flex-col`}
                style={{
                    transition: 'all 0.3s ease-in-out',
                    boxShadow: isHovered ? '0 15px 35px rgba(0, 51, 153, 0.25)' : '0 1px 3px rgba(0,0,0,0.1)',
                    transform: isHovered ? 'translateY(-6px)' : 'none'
                }}
                onMouseEnter={() => setIsHovered(true)}
                onMouseLeave={() => setIsHovered(false)}
            >
                {/* Image Section */}
                <div className="relative aspect-[4/5] md:aspect-square overflow-hidden">
                    <Link to={`/project-details/${item.slug || item._id}`} className="block w-full h-full relative z-0">
                        <img
                            src={getImageUrl(projectImage)}
                            alt={item.name}
                            loading="lazy"
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        {/* Bottom dark overlay for text */}
                        <div className="absolute inset-x-0 bottom-0 h-20 md:h-32 bg-gradient-to-t from-black/95 to-transparent"></div>
                    </Link>

                    {/* Similar Badge */}
                    <div className="absolute top-2 left-2 md:top-4 md:left-4 bg-[#ffbb00] text-black px-1.5 py-0.5 md:px-4 md:py-1.5 rounded-md md:rounded-lg font-bold text-[10px] md:text-[13px] shadow-md flex items-center gap-1 md:gap-2 z-10 pointer-events-none">
                        <i className="fa-solid fa-layer-group text-[7px] md:text-xs"></i> Similar
                    </div>

                    {/* Bottom Price Overlay inside Image */}
                    <div className="absolute bottom-2 left-2 right-2 md:bottom-4 md:left-4 md:right-4 flex justify-start items-end z-20 pointer-events-none">
                        <div className="text-white font-bold text-[1rem] md:text-[1.2rem] drop-shadow-lg leading-tight">
                            {formatPriceRange(item)}
                        </div>
                    </div>

                    {/* Corner Actions (Heart / Compare) */}
                    <div className="absolute top-2 right-2 md:top-4 md:right-4 flex flex-col gap-1 md:gap-2 z-20">
                        <button
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                addToCompare(item);
                            }}
                            className={`w-6 h-6 md:w-8 md:h-8 lg:w-9 lg:h-9 rounded-full backdrop-blur-md border border-white/20 flex items-center justify-center transition-all duration-300 shadow-lg
                                ${compareList.some(cp => cp._id === item._id) 
                                    ? 'bg-yellow-400 text-black border-yellow-400' 
                                    : 'bg-white/10 text-white hover:bg-white hover:text-primary'}`}
                            title="Compare Property"
                        >
                            <i className={`fa-solid ${compareList.some(cp => cp._id === item._id) ? 'fa-check' : 'fa-plus'} text-[8px] md:text-xs lg:text-sm`}></i>
                        </button>
                    </div>
                </div>

                {/* Content Section */}
                <div className="px-2 pb-2 pt-2 md:px-5 md:pb-2 md:pt-3 lg:px-6 lg:pb-3 lg:pt-4 flex flex-col flex-grow bg-white relative z-10 text-left">
                    <h4 className={`text-[15px] md:text-[18px] lg:text-[20px] font-bold text-black mb-1 md:mb-1.5`}>
                        <Link to={`/project-details/${item.slug || item._id}`} className="hover:text-accent transition-colors block">{item.name}</Link>
                    </h4>
                    {/* Address */}
                    <div className="text-black text-[10px] md:text-[13px] lg:text-[14px] mb-1.5 md:mb-2 flex items-start gap-1 md:gap-2 line-clamp-1 overflow-hidden leading-tight font-medium">
                        <i className="fa-solid fa-location-dot mt-0.5 text-black text-[9px] md:text-[12px] lg:text-[14px]"></i>
                        <span className="truncate">{item.address || item.location || item.city || 'Address Not Available'}</span>
                    </div>

                    {/* Specifications - 2x2 Grid with horizontal line separator */}
                    <div className="mt-auto border-t border-gray-100 pt-2 space-y-1.5 md:space-y-2">
                        {/* Row 1 */}
                        <div className="flex items-center justify-between text-black text-[9px] md:text-[13px] font-bold">
                            <div className="flex items-center gap-1.5 md:gap-2 flex-1">
                                <i className="fa-solid fa-bed text-black text-[10px] md:text-[14px] w-3 md:w-5"></i>
                                <span className="truncate">{item.overview?.configuration?.split(',')[0] || '2/3 BHK'}</span>
                            </div>
                            <div className="flex items-center gap-1.5 md:gap-2 flex-1 justify-end text-right">
                                <i className="fa-solid fa-maximize text-black text-[10px] md:text-[14px] w-3 md:w-5"></i>
                                <span className="truncate">{item.area || item.overview?.area || '1200 sq.ft'}</span>
                            </div>
                        </div>
                        {/* Row 2 */}
                        <div className="flex items-center justify-between text-black text-[9px] md:text-[13px] font-bold pt-1">
                            <div className="flex items-center gap-1.5 md:gap-2 flex-1">
                                <i className="fa-solid fa-city text-black text-[10px] md:text-[14px] w-3 md:w-5"></i>
                                <span className="truncate">{item.overview?.totalTowers || '4 Towers'}</span>
                            </div>
                            <div className="flex items-center gap-1.5 md:gap-2 flex-1 justify-end text-right">
                                <i className="fa-solid fa-house-chimney text-black text-[10px] md:text-[14px] w-3 md:w-5"></i>
                                <span className="truncate">{item.overview?.totalUnits || '168 Units'}</span>
                            </div>
                        </div>
                    </div>

                    {/* Bottom View Details Button */}
                    <div className="pt-2.5 md:pt-3">
                        <Link
                            to={`/project-details/${item.slug || item._id}`}
                            className="w-full h-8 md:h-12 bg-[#ffbb00] text-black rounded-md md:rounded-xl flex items-center justify-center font-bold text-[12px] md:text-[14px] hover:bg-[#e6a800] transition-all shadow-sm hover:-translate-y-0.5 whitespace-nowrap"
                        >
                            View Details <i className="fa-solid fa-chevron-right ml-1 md:ml-2.5 text-[7px] md:text-[10px]"></i>
                        </Link>
                    </div>
                </div>
            </div>
        );
    };

    const settings = {
        dots: false,
        infinite: similarProjects.length > 2,
        speed: 500,
        slidesToShow: 4,
        slidesToScroll: 1,
        autoplay: false,
        arrows: true,
        nextArrow: <NextArrow />,
        prevArrow: <PrevArrow />,
        responsive: [
            {
                breakpoint: 1200,
                settings: {
                    slidesToShow: 3,
                    infinite: similarProjects.length > 3,
                }
            },
            {
                breakpoint: 992,
                settings: {
                    slidesToShow: 2.2,
                    infinite: similarProjects.length > 2,
                }
            },
            {
                breakpoint: 768,
                settings: {
                    slidesToShow: 2,
                    arrows: false,
                    centerMode: true,
                    centerPadding: '0px',
                    infinite: similarProjects.length > 2,
                }
            }
        ]
    };

    return (
        <section className="relative bg-[#F4F6F9] pb-12 pt-8 md:pt-12 mt-12 mb-12 -mx-6 px-6">
            <style>
                {`
                .slick-slide > div { margin: 0 12px; }
                .slick-list { margin: 0 -12px; }
                
                @media (max-width: 640px) {
                    .slick-slide > div { margin: 0 1.5px; }
                    .slick-list { margin: 0 -1.5px; }
                }
                `}
            </style>

            <div className="container mx-auto px-0 md:px-4 relative z-10">
                <div className="flex justify-center mb-10">
                    <div className="lg:w-8/12 md:w-10/12 text-center">
                        <div className="sec-heading text-center">
                            <div className="flex items-center justify-center gap-3 md:gap-5 mb-3">
                                <div className="w-12 h-[2px] bg-accent"></div>
                                <span className="text-accent font-bold text-[13px] md:text-[15px] tracking-wide block">Handpicked For You</span>
                                <div className="w-12 h-[2px] bg-accent"></div>
                            </div>
                            <h2 className="text-black font-bold text-[24px] md:text-[32px] leading-tight mb-3">Similar Projects</h2>
                            <div className="w-[80px] md:w-[120px] h-1.5 bg-accent mx-auto rounded-full"></div>
                        </div>
                    </div>
                </div>

                <div className="relative px-0 md:px-4 lg:px-8">
                    <Slider ref={sliderRef} {...settings}>
                        {similarProjects.map((item: any) => (
                            <div key={item._id} className="h-full">
                                <ProjectCard item={item} />
                            </div>
                        ))}
                    </Slider>
                </div>
            </div>
        </section>
    );
};

export default SimilarProjectsSection;